import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Drawer } from "vaul";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { protectionPriceError } from "@crypto-trading/common/browser";
import {
  defaultProtectionPrice,
  formatProtectionPrice,
  formatSignedPercent,
  formatSignedUsd,
  protectionOutcome,
  protectionPills,
  roundProtectionPrice,
  type PositionSide,
  type ProtectionKind,
} from "@/lib/position-protection";
import { protectionErrorMessage } from "@/hooks/usePositionProtection";

export interface ProtectionEditorPosition {
  symbol: string;
  side: PositionSide;
  /** As the api sent it, for its decimals. */
  entryPriceText: string | number;
  entryPrice: number;
  leverage: number;
  usdAmount: number;
}

interface PositionProtectionEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: ProtectionKind;
  position: ProtectionEditorPosition;
  markPrice: number | null;
  liquidationPrice: number | null;
  /** The current TP/SL, if there is one. */
  currentPrice: number | null;
  /** Setting a fixed TP turns trailing take profit off. */
  turnsOffTrailing?: boolean;
  /** Resolves once the exchange has the new order; throws with the api's message. */
  onSave: (price: number) => Promise<void>;
  variant: "mobile" | "desktop";
}

const SHEET_WIDTH = 440;

const sectionClass =
  "rounded-xl border border-white/10 bg-[rgba(13,18,26,0.7)] p-4 space-y-3";
const inputClass =
  "w-full rounded-xl border border-white/10 bg-[#0a0a0a]/60 p-2.5 text-sm text-white focus:border-[#2dcc89] focus:outline-none tabular-nums";

const parsePrice = (text: string): number | null => {
  const normalized = text.replace(",", ".").trim();
  if (!normalized) return null;
  const value = Number(normalized);
  return Number.isFinite(value) ? value : NaN;
};

export const PositionProtectionEditor: React.FC<
  PositionProtectionEditorProps
> = ({
  open,
  onOpenChange,
  kind,
  position,
  markPrice,
  liquidationPrice,
  currentPrice,
  turnsOffTrailing = false,
  onSave,
  variant,
}) => {
  const isTakeProfit = kind === "takeProfit";
  const label = isTakeProfit ? "Take profit" : "Stop loss";
  const [draft, setDraft] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Prefill each time the sheet opens: the current value, else a default.
  useEffect(() => {
    if (!open) return;
    setServerError(null);
    setSaving(false);
    setDraft(
      currentPrice
        ? roundProtectionPrice(currentPrice, position.entryPriceText)
        : defaultProtectionPrice({
            kind,
            entryPrice: position.entryPrice,
            entryPriceText: position.entryPriceText,
            markPrice,
            side: position.side,
            liquidationPrice,
          })
    );
    // Only on open: a live mark tick must not overwrite what the user typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kind]);

  const pills = useMemo(
    () =>
      protectionPills({
        kind,
        entryPrice: position.entryPrice,
        entryPriceText: position.entryPriceText,
        side: position.side,
        liquidationPrice,
      }),
    [kind, position.entryPrice, position.entryPriceText, position.side, liquidationPrice]
  );

  const price = parsePrice(draft);
  const validationError =
    price === null
      ? null
      : protectionPriceError({
          kind,
          price: Number.isNaN(price) ? 0 : price,
          positionType: position.side,
          markPrice: markPrice ?? 0,
          liquidationPrice,
          entryPrice: position.entryPrice,
          leverage: position.leverage,
        });
  const isValid = price !== null && !Number.isNaN(price) && !validationError;
  const unchanged =
    isValid && currentPrice !== null && Math.abs(price - currentPrice) < 1e-12;
  const outcome = isValid
    ? protectionOutcome({
        price,
        entryPrice: position.entryPrice,
        side: position.side,
        leverage: position.leverage,
        usdAmount: position.usdAmount,
      })
    : null;

  const saveLabel = `${currentPrice ? "Update" : "Set"} ${label.toLowerCase()}`;

  const handleSave = async () => {
    if (!isValid || saving) return;
    setServerError(null);
    setSaving(true);
    try {
      await onSave(price);
      onOpenChange(false);
    } catch (error) {
      setServerError(protectionErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const accent = isTakeProfit ? "text-green-400" : "text-red-400";

  const body = (
    <div className="space-y-4 p-4" data-testid="tpsl-editor">
      {turnsOffTrailing && (
        <div className="flex gap-2 rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-3 text-xs text-yellow-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p>
            This position uses a trailing stop loss. Setting a fixed take
            profit turns the trailing stop loss off.
          </p>
        </div>
      )}

      <div className={sectionClass}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-text-secondary mb-1">Mark price</p>
            <p className="text-sm font-semibold text-text-primary tabular-nums">
              {markPrice
                ? formatProtectionPrice(markPrice, position.entryPriceText)
                : "---"}
            </p>
          </div>
          <div>
            <p className="text-xs text-text-secondary mb-1">Entry price</p>
            <p className="text-sm font-semibold text-text-primary tabular-nums">
              {formatProtectionPrice(position.entryPrice, position.entryPriceText)}
            </p>
          </div>
        </div>
      </div>

      <div className={sectionClass}>
        <label
          htmlFor="tpsl-price-input"
          className="block text-xs font-medium text-[#a3a3a3]"
        >
          {label} price (USDT)
        </label>
        <input
          id="tpsl-price-input"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          className={inputClass}
          value={draft}
          onChange={(event) => {
            setServerError(null);
            setDraft(event.target.value);
          }}
          aria-invalid={Boolean(validationError)}
          aria-describedby="tpsl-price-hint"
        />
        {pills.length > 0 && (
          <div className="flex flex-wrap gap-2" role="group" aria-label="Move from entry">
            {pills.map((pill) => {
              const selected = draft.trim() === pill.value;
              return (
                <button
                  key={pill.percent}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => {
                    setServerError(null);
                    setDraft(pill.value);
                  }}
                  className={`flex-1 min-w-[3.5rem] rounded-full border px-2 py-1 text-xs transition-colors ${
                    selected
                      ? "border-[#2dcc89] bg-[#2dcc89]/15 text-white"
                      : "border-white/15 bg-[#171717] text-white hover:bg-[#262626]"
                  }`}
                >
                  {pill.label}
                </button>
              );
            })}
          </div>
        )}
        <div id="tpsl-price-hint" className="min-h-[1rem]">
          {validationError ? (
            <p className="text-xs text-red-400" role="alert">
              {validationError}
            </p>
          ) : outcome ? (
            <p className="text-xs text-text-secondary">
              Triggers at mark price ·{" "}
              <span className={accent}>
                {formatSignedPercent(outcome.movePercent)}
              </span>{" "}
              from entry · ROI{" "}
              <span className={accent}>
                {formatSignedPercent(outcome.roiPercent, 0)}
              </span>{" "}
              · est. PnL{" "}
              <span className={accent}>{formatSignedUsd(outcome.pnl)}</span>
            </p>
          ) : (
            <p className="text-xs text-text-muted">
              Enter the price that closes the whole position.
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const footer = (
    <div className="space-y-2">
      {serverError && (
        <p className="text-xs text-red-400 text-center" role="alert">
          {serverError}
        </p>
      )}
      <button
        type="button"
        disabled={!isValid || unchanged || saving}
        onClick={handleSave}
        className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-white/10 py-3 font-semibold text-white bg-[#07a66c] hover:bg-[#08b877] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
        {saving ? "Saving..." : saveLabel}
      </button>
    </div>
  );

  const header = (
    <div>
      <p className="text-xs text-text-secondary mb-1">
        {position.symbol} ·{" "}
        <span className={position.side === "LONG" ? "text-green-400" : "text-red-400"}>
          {position.side === "LONG" ? "Long" : "Short"}
        </span>{" "}
        · {position.leverage}x
      </p>
      <h2 className={`text-base font-semibold ${accent}`}>{label}</h2>
    </div>
  );

  if (variant === "desktop") {
    return (
      <DesktopEditorSheet
        open={open}
        onOpenChange={onOpenChange}
        ariaLabel={label}
        header={header}
        footer={footer}
      >
        {body}
      </DesktopEditorSheet>
    );
  }

  return (
    <Drawer.NestedRoot
      open={open}
      onOpenChange={onOpenChange}
      repositionInputs={false}
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/80 z-[120]" />
        <Drawer.Content
          className="bg-primary flex rounded-t-3xl flex-col fixed bottom-0 left-0 right-0 max-h-[95vh] z-[121]"
          aria-describedby={undefined}
        >
          <div className="px-4 border-b border-white/10 pb-3 pt-3 sticky top-0 bg-primary z-10 rounded-t-3xl">
            <Drawer.Handle className="mx-auto mb-3 mt-0 h-1 w-10 rounded-full" />
            <div className="flex items-start justify-between">
              <Drawer.Title asChild>{header}</Drawer.Title>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                aria-label="Close editor"
                className="p-2 -mr-2 rounded-lg hover:bg-white/5"
              >
                <X className="w-5 h-5 text-text-secondary" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">{body}</div>
          <div
            className="sticky bottom-0 border-t border-white/10 bg-primary/90 p-4 backdrop-blur-md"
            style={{
              paddingBottom:
                "calc(var(--app-safe-bottom, env(safe-area-inset-bottom, 0px)) + 16px)",
            }}
          >
            {footer}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.NestedRoot>
  );
};

/** Right-side sheet over desktop Position Details, like the trade sheet. */
const DesktopEditorSheet: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ariaLabel: string;
  header: React.ReactNode;
  footer: React.ReactNode;
  children: React.ReactNode;
}> = ({ open, onOpenChange, ariaLabel, header, footer, children }) => {
  useEffect(() => {
    if (!open) return;
    // Capture on window so Escape closes this sheet only, not Position
    // Details underneath (it listens on document).
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onOpenChange(false);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open, onOpenChange]);

  if (!open) return null;
  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/60 z-[120]"
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className="fixed top-0 right-0 h-full bg-primary border-l border-white/10 flex flex-col shadow-2xl z-[121]"
        style={{ width: `${SHEET_WIDTH}px` }}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-white/10">
          {header}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close editor"
            className="p-2 rounded-lg hover:bg-white/5 text-text-secondary hover:text-text-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
        <div className="border-t border-white/10 bg-primary/90 p-4">{footer}</div>
      </div>
    </>,
    document.body
  );
};
